/**
 * ReviewCard — a single approved review rendered inside the wall.
 *
 * Layout: dark surface card with a champagne hairline top border. Left
 * column carries reviewer identity + stars, right column carries the
 * review body, service badge, photo thumbnails, and date. Long bodies
 * (>400 chars) truncate with a click-to-expand affordance.
 *
 * Photo clicks open the ReviewPhotoLightbox at the correct index. The
 * date is computed once at render — it's a static formatted string so
 * there's no hydration flicker.
 *
 * data-testids:
 *   review-card, review-stars, review-text, review-service-badge,
 *   review-photo-thumb-{index}, review-expand
 */

import { useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import StarRating from "./StarRating";
import { reviewPhotoUrl } from "../../lib/reviewsApi";
import { SERVICES } from "../../data/content";

const TRUNCATE_AT = 400;

function serviceName(slug) {
    if (!slug) return null;
    const s = SERVICES.find((x) => x.slug === slug);
    return s ? s.name : null;
}

function relativeDate(iso) {
    if (!iso) return "";
    try {
        return formatDistanceToNow(parseISO(iso), { addSuffix: true });
    } catch {
        return "";
    }
}

export default function ReviewCard({ review, onOpenLightbox }) {
    const [expanded, setExpanded] = useState(false);
    const bodyLong = review.review_text.length > TRUNCATE_AT;
    const body =
        !bodyLong || expanded
            ? review.review_text
            : `${review.review_text.slice(0, TRUNCATE_AT).trimEnd()}…`;

    const dateStr = relativeDate(review.approved_at || review.submitted_at);
    const svc = serviceName(review.service_used);
    const photos = review.photos || [];

    return (
        <article
            className="relative bg-[#15161A] border border-[rgba(201,168,106,0.15)] p-8 md:p-10 transition-colors duration-300 hover:border-[rgba(201,168,106,0.4)]"
            data-testid="review-card"
            data-review-id={review.id}
        >
            {/* Top hairline accent */}
            <div className="absolute top-0 left-0 h-px w-16 bg-[#C9A86A]" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                {/* Left column — identity */}
                <div className="md:col-span-4">
                    <StarRating
                        value={review.star_rating}
                        size={18}
                        data-testid="review-stars"
                    />
                    <div className="mt-4 font-serif text-2xl md:text-[26px] text-[#F3EFE7] leading-tight">
                        {review.reviewer_name}
                    </div>
                    <div className="mt-3 text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A]">
                        {dateStr}
                    </div>
                </div>

                {/* Right column — content */}
                <div className="md:col-span-8">
                    {svc && (
                        <span
                            className="inline-block text-[10px] tracking-[0.32em] uppercase text-[#C9A86A] border border-[rgba(201,168,106,0.4)] px-3 py-1 mb-4"
                            data-testid="review-service-badge"
                        >
                            {svc}
                        </span>
                    )}

                    <p
                        className="text-[15px] leading-[1.85] text-[#F3EFE7] font-serif italic"
                        data-testid="review-text"
                    >
                        {body}
                    </p>

                    {bodyLong && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className="mt-3 text-[10.5px] tracking-[0.28em] uppercase text-[#C9A86A] hover:text-[#D9BC80] transition-colors"
                            data-testid="review-expand"
                        >
                            {expanded ? "Read less" : "Read more"}
                        </button>
                    )}

                    {photos.length > 0 && (
                        <div
                            className="mt-6 flex flex-wrap gap-3"
                            data-testid="review-photos"
                        >
                            {photos.map((p, i) => (
                                <button
                                    key={p.url}
                                    type="button"
                                    onClick={() => onOpenLightbox?.(photos, i)}
                                    className="group relative w-24 h-24 md:w-28 md:h-28 overflow-hidden border border-[rgba(201,168,106,0.2)] hover:border-[#C9A86A]/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A]/60"
                                    data-testid={`review-photo-thumb-${i}`}
                                    aria-label={`Open photo ${i + 1}`}
                                >
                                    <img
                                        src={reviewPhotoUrl(p.url)}
                                        alt={p.original_filename || `Review photo ${i + 1}`}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}
